import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getBlogPost, getRelatedBlogPosts } from '../lib/api';
import { useSEO, buildBlogSchema, buildBreadcrumb } from '../lib/seo';
import './BlogPostPage.css';

const CATEGORY_LABEL = {
  'plant-care': 'Plant Care',
  lifestyle: 'Lifestyle',
  guides: 'Guides',
  sustainability: 'Sustainability',
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Block({ block }) {
  if (block.type === 'h2') return <h2 className="bd-h2">{block.text}</h2>;
  if (block.type === 'quote') {
    return (
      <blockquote className="bd-quote">
        <svg width="28" height="22" viewBox="0 0 28 22" fill="none" className="bd-quote__mark">
          <path d="M8.4 0C3.7 2.6 0 7.7 0 13.1c0 4.3 2.7 7.4 6.3 7.4 3 0 5.4-2.3 5.4-5.3 0-2.8-2-4.9-4.6-4.9-.5 0-1 .1-1.4.2C6.1 7.1 8.6 4 12 2.2L8.4 0Zm14.3 0c-4.7 2.6-8.4 7.7-8.4 13.1 0 4.3 2.7 7.4 6.3 7.4 3 0 5.4-2.3 5.4-5.3 0-2.8-2-4.9-4.6-4.9-.5 0-1 .1-1.4.2C20.4 7.1 22.9 4 26.3 2.2L22.7 0Z" fill="currentColor"/>
        </svg>
        {block.text}
      </blockquote>
    );
  }
  return <p className="bd-p">{block.text}</p>;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);

    getBlogPost(slug)
      .then(data => {
        if (!active) return;
        setPost(data);
        return getRelatedBlogPosts(slug, 3).then(r => { if (active) setRelated(r.posts || []); });
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [slug]);

  // ── SEO ──────────────────────────────────────────────────────────────────
  useSEO({
    title: post ? post.title : 'Naturiq Blog — Plant Care, Lifestyle & Guides',
    description: post
      ? (post.excerpt || `${post.title} — Read on the Naturiq blog.`).slice(0, 160)
      : 'Expert plant care tips, lifestyle guides and sustainability insights from Naturiq.',
    image: post?.cover || undefined,
    url: `/blog/${slug}`,
    type: 'article',
    structuredData: post
      ? [
          buildBlogSchema(post),
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: post.title, url: `/blog/${slug}` },
          ]),
        ]
      : null,
  });

  if (loading) {
    return (
      <main className="bd-page">
        <div className="bd-skel">
          <div className="bd-skel__hero skel" />
          <div className="bd-skel__body">
            <div className="skel" style={{ width: '20%', height: 13, borderRadius: 6 }} />
            <div className="skel" style={{ width: '80%', height: 36, borderRadius: 8, marginTop: 12 }} />
            <div className="skel" style={{ width: '55%', height: 36, borderRadius: 8, marginTop: 8 }} />
            <div className="skel" style={{ width: '35%', height: 13, borderRadius: 6, marginTop: 16 }} />
            <div className="bd-skel__lines">
              {[100, 95, 88, 92, 70, 96, 83, 60].map((w, i) => (
                <div key={i} className="skel" style={{ width: `${w}%`, height: 13, borderRadius: 6 }} />
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="bd-page bd-notfound">
        <h1>We couldn't find that story.</h1>
        <p>It may have been moved, or the link might be off by a leaf.</p>
        <button onClick={() => navigate('/blog')} className="bd-back-btn">Back to the journal</button>
      </main>
    );
  }

  return (
    <main className="bd-page">

      {/* ── Hero ── */}
      <section className="bd-hero">
        <img src={post.cover} alt={post.title} className="bd-hero__img" />
        <div className="bd-hero__overlay" />
        <div className="bd-hero__content">
          <Link to="/blog" className="bd-hero__back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Garden Journal
          </Link>
          <span className="bd-hero__tag">{CATEGORY_LABEL[post.category] || post.category}</span>
          <h1 className="bd-hero__title">{post.title}</h1>
          <div className="bd-hero__meta">
            <img src={post.author?.avatar} alt={post.author?.name} className="bd-hero__avatar" />
            <div>
              <span className="bd-hero__author">{post.author?.name}</span>
              <span className="bd-hero__sep">·</span>
              <span>{formatDate(post.date)}</span>
              <span className="bd-hero__sep">·</span>
              <span>{post.readTime} min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Article ── */}
      <section className="bd-article">
        <article className="bd-article__body">
          {post.content.map((block, i) => <Block key={i} block={block} />)}
        </article>

        {/* Share + author */}
        <aside className="bd-sidebar">
          <div className="bd-share">
            <span className="bd-share__label">Share this</span>
            <div className="bd-share__row">
              {['WhatsApp', 'X', 'Pinterest', 'Copy link'].map(s => (
                <button key={s} className="bd-share__btn" type="button" aria-label={`Share on ${s}`}>
                  {s === 'WhatsApp' && (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l4.9-1.3A10 10 0 1 0 12 2z" opacity="0"/><path d="M17.5 14.4c-.3-.1-1.6-.8-1.8-.9-.2-.1-.4-.1-.6.1-.2.2-.6.9-.8 1-.1.2-.3.2-.5.1-1.4-.6-2.3-1.4-3.1-2.7-.2-.3 0-.4.2-.6.2-.2.3-.4.4-.6.1-.2 0-.4 0-.5-.1-.1-.6-1.5-.8-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.4-.2.3-.9 1-.9 2.3 0 1.3 1 2.6 1.1 2.8.1.2 1.9 3 4.7 4 2.8 1 2.8.7 3.3.6.5 0 1.6-.6 1.8-1.3.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.6-.3z"/><path d="M12 2a10 10 0 1 0 5.7 18.2L21 21l-1-3.4A10 10 0 0 0 12 2zm0 1.6a8.4 8.4 0 0 1 7 13l-.2.4.7 2.4-2.5-.7-.4.2A8.4 8.4 0 1 1 12 3.6z"/></svg>
                  )}
                  {s === 'X' && (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.3 2H21l-6.4 7.4L22 22h-6.9l-5.4-7-6.2 7H1l6.9-7.9L1 2h7l4.9 6.5L18.3 2Zm-2.4 18h1.9L8.2 4H6.2l9.7 16Z"/></svg>
                  )}
                  {s === 'Pinterest' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 3 5.7 3 10c0 2.6 1.4 4.6 3.5 5.4.3.1.4 0 .5-.3l.4-1.4c.1-.3 0-.4-.2-.6-.6-.6-1-1.6-1-2.8 0-3.6 2.7-6.6 7-6.6 3.7 0 5.8 2.2 5.8 5.2 0 4-1.8 6.8-4.2 6.8-1.3 0-2.3-1.1-2-2.4.4-1.7 1.2-3.5 1.2-4.7 0-1.1-.6-2-1.8-2-1.4 0-2.6 1.5-2.6 3.5 0 1.3.4 2.1.4 2.1s-1.6 6.6-1.8 7.8c-.4 1.7-.1 3.7-.1 3.9 0 .1.2.2.3.1 1.3-1.7 2.2-4.2 2.2-4.2.2-.6.9-3.4.9-3.4.5.9 1.8 1.6 3.2 1.6 4.2 0 7.1-3.8 7.1-8.9C21 5.5 17.2 2 12 2z"/></svg>
                  )}
                  {s === 'Copy link' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.5.4l2-2a5 5 0 0 0-7-7l-1.1 1"/><path d="M14 11a5 5 0 0 0-7.5-.4l-2 2a5 5 0 0 0 7 7l1-1"/></svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bd-author-card">
            <img src={post.author?.avatar} alt={post.author?.name} className="bd-author-card__avatar" />
            <h3 className="bd-author-card__name">{post.author?.name}</h3>
            <p className="bd-author-card__role">{post.author?.role}</p>
            <p className="bd-author-card__bio">
              Writes for the Naturiq Garden Journal — practical plant care notes,
              tested on our own farm before they reach the page.
            </p>
          </div>
        </aside>
      </section>

      {/* ── Related posts ── */}
      {related.length > 0 && (
        <section className="bd-related">
          <h2 className="bd-related__title">More from the journal</h2>
          <div className="bd-related__grid">
            {related.map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="bd-related__card">
                <div className="bd-related__img-wrap">
                  <img src={p.cover} alt={p.title} className="bd-related__img" loading="lazy" />
                </div>
                <div className="bd-related__body">
                  <span className="bp-tag">{CATEGORY_LABEL[p.category] || p.category}</span>
                  <h3 className="bd-related__card-title">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

    </main>
  );
}